import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import isEmpty from "lodash/isEmpty";
import { observer } from "mobx-react-lite";
import { useEffectOnce, useForceUpdate } from "@lib-shared/hooks";
import { BusinessPage } from "core/constants";
import { AppContext } from "core/context";
import { Feature, isEnabled } from "core/features";
import { logApiError } from "core/lib";
import { dateWrapper } from "core/lib/date.wrapper";
import UserActivityLogStore from "core/stores/user-activity-logger.store";
import { direction, UserActivityLogStoreManager } from "core/stores/user-activity-logger.store.manager";
import { ModalAlert, PleaseWait, SquareLoader } from "components/common-ui";
import Filter from "./components/filter";
import ItemRows from "./components/item-rows";
import Pager from "components/pager";

const activityLogsStore = new UserActivityLogStore();

const UserActivityLogs = observer(() => {
    const appContext = useContext(AppContext);
    const initialDateRangeInMonths = 2;
    const navigate = useNavigate();

    const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
    const [dataCount, setDataCount] = useState(0); //used to trigger getData();

    const forceUpdate = useForceUpdate();

    const endDate = new Date();

    const startDate = dateWrapper().addMonths(0 - initialDateRangeInMonths).date;
    const maxDate = new Date();

    const getInitialState = () => {
        return {
            ciamId: "",
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            maxDate: new Date(maxDate),
            autoUpdate: true
        };
    };

    const [state, setState] = useState(getInitialState());

    const store = activityLogsStore;

    //DataManager used to get data from User Activity Logging api
    const onError = (error: any) => {
        const message = "Error getting user activity log";

        ModalAlert.error(message,);

        console.error(message, error);
    }

    const onUsersError = (error: any) => {
        const message = "Error getting users";

        ModalAlert.error(message,);

        console.error(message, error);
    }

    const dataManager = new UserActivityLogStoreManager(onError, onUsersError, activityLogsStore);

    const title = "Activity Log";

    const toggleSort = (field: string) => {
        const currentField = store.sorting.field;

        const hasFieldChanged = field === currentField

        const dir = hasFieldChanged
            ? direction.toggle(store.sorting.direction)
            : direction.desc;

        store.updateSorting({
            field: field,
            direction: dir,
            hasFieldChanged: hasFieldChanged
        });
    }

    const getUsersAsync = async () => {
        await dataManager.getUsersAsync();

        setHasLoadedUsers(true);
    }

    const getItemsAsync = async (currentState: any = null) => {
        currentState = currentState ?? state;
        store.updateIsLoading(true);

        try {
            await dataManager.getLogsAsync(currentState);
        } catch (error) {
            handleErrorGettingItems(error);
        } finally {
            store.updateIsLoading(false);
            forceUpdate();
        }
    }

    const handleErrorGettingItems = (error: any) => {
        store.updateItems([]);

        if (error === null) {
            return;
        }

        logApiError(error);

        const message = "Error getting logs";

        ModalAlert.error(message);
    }

    useEffectOnce(() => {
        const onLoad = async () => {
            appContext.updateTitle(title);
            appContext.updateHeader(title);

            if (!isEnabled(Feature.users_activity_log)) {
                ModalAlert.permissionsError(() => navigate(BusinessPage.dashboard));
            }

            await getUsersAsync();

            await getItemsAsync();
        }

        onLoad();
    });

    const handleFilterChange = (controlName: string, newValue: string, defaultValue = "", autoUpdate = true) => {
        try {
            const newState = {
                ...state,
                autoUpdate: autoUpdate,
                [controlName]: newValue ?? defaultValue
            };

            setState(newState)

            getItemsAsync(newState);
        } catch (error) {
            console.error(error);
        }

        setDataCount(dataCount + 1);
    }

    const download = async () => {
        const dialog = ModalAlert.alert("Download", "You export will be downloaded shortly");

        try {
            await dataManager.downloadAsync("user-logs.xlsx", state);
        } catch (error) {
            if (dialog != null) {
                dialog.close();
            }

            const message = "Error downloading user logs export";
            ModalAlert.error(message);

            logApiError(error);
        }
    }

    if (isEmpty(state)) {
        return null;
    }

    if (!hasLoadedUsers) {
        return <PleaseWait message="Getting users" />;
    }

    return (
        <section id="user-activity-logs" className="floaty-form">
            <section className="user-activities-container">
                <form className="filter floating">
                    <span>
                        {!store.isLoading && <button type="button" className="download-mobile" onClick={download} title="Export to Excel">
                            <i></i>
                        </button>}
                    </span>

                    <Filter
                        state={state}
                        users={store.users}
                        isLoading={store.isLoading}
                        download={download}
                        handleChange={handleFilterChange} />
                </form>

                {
                    store.isLoading
                        ? <SquareLoader />
                        : <ItemRows store={store} toggleSort={toggleSort} />
                }
            </section>

            {!store.isLoading && <Pager
                isLoading={store.isLoading}
                currentItems={store.pageItems}
                pageCount={store.numPages}
                currentPage={store.currentPage}
                handlePageClick={(e: any) => {
                    store.updateCurrentPage(e.selected);
                    forceUpdate();
                }} />}
        </section>
    );
});

export default UserActivityLogs;
